(ns app.front.entities
  "Frontend entity implementation helpers for re-frame.

   The frontend has a copy of a subset of the datomic database. Entities that implement the datomic
   entity API - notably the navigation between entities via ref attributes - can be fetched with these
   subscriptions. Unlike the backend datomic, equality is defined by content, not by id, so a change
   in an entity will cause any subscriptions depending on it or any other entity reachable with
   keyword navigation to also update.

   For keyword navigation to work, the destination entity needs to be loaded to the entity-db. If only
   the id (or presence of another entity) needs to be checked, there is `get-id` to do that. It works
   for getting the id of a destination entity for a ref attr even when the entity is not loaded to the
   frontend.

   These entities will contain the attribute `::gen` which allows for updating based on related entities
   changing. To keep ::gen up to date, don't update ::entity-db directly in the re-frame app-db, use the
   bump- functions or events defined here.

   Most useful subscriptions. Some of them have a function exposed as well for a synchronous operation
   to be used in event or subscription handlers - prefer the subscription variant.

   `::find-entities`, `::find-entity`
   get one or multiple entities from the entity-db

   `::apply-changes`
   Change the entity-db based on a transaction-describing response from the backend (produced with
   `outject/tx-changes`)

   `::override-entity-db`
   Use for complete entity maps from backend queries: removes omitted attributes, keeps other entities.
   Use `::merge-entity-db` only for partial attributes; it keeps omitted attributes.

   `::remove-from-entity-db`, `::remove-entity`, `::remove-entities`, `::override-entity-db-for-all`,
   `::merge-entity-db`, `::update-entity`
   Change the entity-db directly in more specific ways"
  (:require [clojure.set :as set]
            [core.db.entity-api :as entity-api]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(defn- key-when-ref
  "Given entity-schema and a set of ids, return the key of a kvp
  if said kvp has a ref attribute pointing to any of the given ids."
  [schema ids [k v]]
  (when (some (fn [[attr v]]
                (case (get schema attr)
                  :db.cardinality/one (contains? ids v)
                  :db.cardinality/many (some ids v)
                  false))
              v)
    k))


(defn- add-outgoing-refs [schema all-refs [_ e]]
  (reduce (fn [refs [k v]]
            (case (get schema k)
              :db.cardinality/many (set/union refs (set v))
              :db.cardinality/one (conj refs v)
              refs))
          all-refs
          e))


(defn- entities-that-ref
  "Find all entity-ids that refer to this id, or are referred by it"
  ([schema entity-db ids]
   (entities-that-ref schema entity-db ids #{}))
  ([schema entity-db ids ids-already-seen]
   (let [ids (set/difference ids ids-already-seen)]
     (when (seq ids)
       (let [incoming-refs (->> entity-db
                                (keep (partial key-when-ref schema ids))
                                set)
             outgoing-refs (->> (select-keys entity-db ids)
                                (reduce (partial add-outgoing-refs schema) #{}))
             refs          (set/union incoming-refs outgoing-refs)]
         (->> (entities-that-ref schema entity-db refs (set/union ids ids-already-seen))
              (set/union refs)))))))


(defn- bump-generations [ids entity-db]
  (reduce (fn [db id]
            ;; new generation - forces re-render for views that depend on this entity
            (update-in db [id ::gen] inc))
          entity-db
          ids))


(defn- bump-generations-related-to [ids schema old-entity-db entity-db]
  (let [referrers (set/union (entities-that-ref schema old-entity-db ids)
                             (entities-that-ref schema entity-db ids))]
    (bump-generations referrers entity-db)))


(defn- merge-preserving-generations
  "Merge whole entity maps while preserving existing `::gen` values
   so generation bumps remain monotonic and dependent subscriptions keep updating."
  [old-entity-db m]
  (merge old-entity-db
         (reduce-kv (fn [acc id entity]
                      (if-some [gen (get-in old-entity-db [id ::gen])]
                        (assoc acc id (assoc entity ::gen gen))
                        acc))
                    m
                    m)))

(defn- update-and-bump-entity-db
  "Update `::entity-db` within `db` by calling `update-f` with old
  `::entity-db` as the first argument and `m` as the second.

  Take keys of `m` and bump all entities that refer to those,
  or are referred to by them in the updated db.

  'bump' means to increment the ::gen attribute for all entities that
  can be used to navigate to any entity update in this command. This
  is necessary, because the frontend rendering may change even if a
  specific entity which is given as a parameter to a view or
  subscription does not itself change"
  [db m update-f]
  (update db ::entity-db (comp (partial bump-generations-related-to
                                        (-> m keys set)
                                        (::schema db)
                                        (::entity-db db))
                               #(update-f % m))))

;;
;; Utilities to find entities or an entity:
;;


(defn find-entities
  "Scan entity-db for entities that match the provided `match`. The `match` can be
  a map or transducer xform."
  [{:keys [entity-db] :as db} match]
  (cond
    (nil? match)
    nil

    (map? match)
    (entity-api/query db match)

    ; Set of ids
    (set? match)
    (map #(entity-api/entity db %) match)

    (integer? match)
    (throw (ex-info "find-entities with integer `match`, use find-entity instead" {:match match}))

    (fn? match)
    (->> entity-db
         vals
         (sequence match)
         (map #(entity-api/entity db (:db/id %))))

    :else
    (throw (ex-info (str "find-entities with unknown match: " match) {:match match}))))


(defn find-entity
  "Scan entity-db for entity that match the provided `match`. The `match` can be
  a map, an entity ID, or transducer xform."
  [{:keys [entity-db] :as db} match]
  (cond
    (nil? match)
    nil

    (integer? match)
    (entity-api/entity db match)

    (map? match)
    (entity-api/find db match)

    (fn? match)
    (->> entity-db
         vals
         (sequence match)
         first
         :db/id
         (entity-api/entity db))

    :else
    (throw (ex-info (str "find-entity with unknown match: " match) {:match match}))))


;;
;; Subscriptions and handlers for entities.
;;
;; In this context `entity-db` refers to a map where keys are entity ID's and values are
;; the entities. The `entities` refers to a seq of entities, i.e. `(vals entity-db)`.
;;


;; Subscription for the entity DB. Entity DB is a map of entities with the entity ID as
;; a key:

(rf/reg-sub ::entity-db
  (fn [db _]
    (-> db ::entity-db)))


;; Subscription for DB schema data:

(rf/reg-sub ::schema
  (fn [db _]
    (-> db ::schema)))

;; Subscription for set of attributes with :db/isComponent
(rf/reg-sub ::components
  (fn [db _]
    (-> db ::components)))

;; Context to pass to find-entities
(rf/reg-sub ::context
  :<- [::entity-db]
  :<- [::schema]
  :<- [::components]
  (fn [[entity-db schema components] _]
    {:entity-db entity-db :schema schema :components components}))

(defn db->context
  "Helper for constructing the context for event handlers"
  [{::keys [entity-db schema components]}]
  {:entity-db entity-db :schema schema :components components})

;; Subscriptions for a seq of entities.

(rf/reg-sub ::entities
  :<- [::context]
  (fn [context [_ match]]
    (find-entities context match)))


;; Subscribe for an entity.
;;
;; If the `match` is an integer, returns the entity by
;; that entity ID.
;;
;; The first entity is returned.

(rf/reg-sub ::entity
  :<- [::context]
  (fn [context [_ xform]]
    (find-entity context xform)))

;; subscribe to entity data as a map, not entity
;; all related entities are included only as ids
(rf/reg-sub ::entity-data
  :<- [::entity-db]
  (fn [entity-db [_ entity-id]]
    (get entity-db entity-id)))

;; subscribe to just one entity attribute
;; if the attribute is type entity, just id is returned
(rf/reg-sub ::entity-attr
  (fn [[_ entity-id _]]
    (rf/subscribe [::entity-data entity-id]))
  (fn [entity-data [_ _ attr]]
    (get entity-data attr)))

;; Subscribe children of an entity
;;
;; Given one or more ids, subscribe recursively all child entities. Note that
;; you must populate the entity-db with the children first.

(defn find-children-recursive [ctx id]
  (when id
    (let [direct-children (find-entities ctx {:entity/parent id})
          children        (->> direct-children
                               (map :db/id)
                               (map (partial find-children-recursive ctx))
                               (apply concat))]
      (->> children
           (cons (find-entity ctx id))
           (filter some?)))))

(rf/reg-sub ::children
  :<- [::context]
  (fn [ctx [_ id-or-ids]]
    (->> (if (seqable? id-or-ids) id-or-ids [id-or-ids])
         (map (partial find-children-recursive ctx))
         (apply concat))))


;;
;; update-entity:
;;
;; Fx handler that updates single entity in entity-db. Accepts an entity ID and
;; a map to merge to the current state of the entity. Attributes with `nil` as
;; value are removed.
;;


(defn- apply-entity-update [entity update-map]
  (->> (merge entity update-map)
       (p/remove-vals nil?)))


(rf/reg-event-fx ::update-entity
  (fn [{:keys [db]} [_ entity-id update-map]]
    {:db (update-and-bump-entity-db db {entity-id update-map} (partial merge-with apply-entity-update))}))


;;
;; merge-entity-db
;;
;; Fx handler that merges the provided entities to entity DB. Designed to be
;; used result event for :comm/command. Can accept any command execution event,
;; but ignores result unless status is 200.
;;

(defn merge-entity-db
  "Merge partial entity attributes and update ::gen. Omitted attributes are kept.
   For complete entity maps, use override-entity-db instead.
   Prefer the ::merge-entity-db event unless a synchronous update is needed."
  [db additional-entity-db]
  (if (map? additional-entity-db)
    (update-and-bump-entity-db db additional-entity-db p/deep-merge)
    (js/console.error (str "data for entity-db is not a map, it is: " (type additional-entity-db)))))

(rf/reg-event-fx ::merge-entity-db
  (fn [{:keys [db]} [_ status additional-entity-db]]
    (when (= status 200)
      {:db (merge-entity-db db additional-entity-db)})))

;;
;; override-entity-db
;;

(defn override-entity-db
  "Apply complete entity maps: remove omitted attributes, keep other entities, and update ::gen.
   Prefer the ::override-entity-db event unless a synchronous update is needed."
  [db additional-entity-db]
  (if (map? additional-entity-db)
    (update-and-bump-entity-db db additional-entity-db merge-preserving-generations)
    (js/console.error (str "data for entity-db is not a map, it is: " (type additional-entity-db)))))

(rf/reg-event-fx ::override-entity-db
  (fn [{:keys [db]} [_ status additional-entity-db]]
    (when (= status 200)
      {:db (override-entity-db db additional-entity-db)})))

(defn remove-entities-fn [db removed-entities]
  (comp (partial bump-generations-related-to
                 (set removed-entities)
                 (::schema db)
                 (::entity-db db))
        #(apply dissoc % removed-entities)))

(rf/reg-event-fx ::remove-from-entity-db
  (fn [{:keys [db]} [_ status {:keys [removed-entities]}]]
    (when (= status 200)
      (if (seqable? removed-entities)
        {:db (update db ::entity-db (remove-entities-fn db removed-entities))}
        (js/console.error (str "data for remove-from-entity-db is not a list, it is: " (type removed-entities)))))))

(defn remove-entities-with-filter [db filterer]
  (let [to-remove (->> db
                       ::entity-db
                       vals
                       (filter (p/where-fn filterer))
                       (keep :db/id))]
    (-> db
        (update ::entity-db (remove-entities-fn db to-remove)))))

(defn- remove-existing-entities-and-merge-new-ones
  "Removes all entities in the entity-db that match the given filter,
  then merges the additional-entity-db with the given strategy"
  [existing-db filter-definition additional-entity-db override?]
  (if (map? additional-entity-db)
    (-> existing-db ;; Not an entity-db, just plain db
        (remove-entities-with-filter filter-definition)
        (update-and-bump-entity-db additional-entity-db (if override?
                                                          merge-preserving-generations
                                                          p/deep-merge)))
    (js/console.error "data for entity-db is not a map, it is: " (type additional-entity-db))))

;; remove all entities that match a given filter, add new ones from update
(rf/reg-event-fx ::override-entity-db-for-all
  (fn [{:keys [db]} [_ filter-definition status additional-entity-db]]
    (when (= status 200)
      {:db (remove-existing-entities-and-merge-new-ones db filter-definition additional-entity-db true)})))

;; Same as ::override-entity-db-for-all except uses deep merge for the entities so no data is lost
(rf/reg-event-fx ::merge-entity-db-for-all
  (fn [{:keys [db]} [_ filter-definition status additional-entity-db]]
    (when (= status 200)
      {:db (remove-existing-entities-and-merge-new-ones db filter-definition additional-entity-db false)})))

;;
;; apply-changes
;;
;; Fx handler that applies changes to entity DB. Designed to be
;; used result event for :comm/command. Can accept any command execution event,
;; but ignores result unless status is 200.
;;
;; Expects the response to be a map of changes, generated by outject marker
;; `core.command.io.outject/tx-changes`.
;;

(defn id-of-some-in-apply-changes
  "Given a result payload formatted for ::apply-changes, return
  id of some newly created entity that has a given type"
  [expected-type result]
  (->> result
       (some (fn [[k {[_ type] :entity/type}]]
               (when (= type expected-type)
                 k)))))

(defn property-of-some-in-apply-changes
  "Given a result for ::apply-changes, get a newly added
  property of some of the entities. Optional filter-fn filters changes prior to matching."
  ([k result]
   (property-of-some-in-apply-changes k result identity))
  ([k result filter-fn]
   (->> result
        vals
        filter-fn
        (some (comp (fn [[op val]] (when (= op :assoc) val))
                    k)))))

(def op-kws #{:assoc :dissoc :conj :disj})

(defn- apply-entity-change [entity [attribute op-or-ops]]
  (let [[[op value] & rest] (if (keyword? (first op-or-ops))
                              [op-or-ops]
                              op-or-ops)]
    (cond-> (case op
              :assoc (assoc entity attribute value)
              :dissoc (dissoc entity attribute)
              :conj (update entity attribute (fn [v] (conj (or v #{}) value)))
              :disj (update entity attribute (comp set (partial remove #(or (= % value)
                                                                            ;; when :attribute/export-as-map? true
                                                                            ;; entity must be filtered by :db/id
                                                                            (= (:db/id %) value))))))
      (seq rest) (apply-entity-change [attribute (if (and (-> rest first vector?)
                                                          (->> rest ffirst (contains? op-kws)))
                                                   rest
                                                   (cons op rest))]))))

(defn- apply-entity-changes [entity entity-id entity-changes]
  (reduce apply-entity-change
          (if entity
            entity
            {:db/id entity-id})
          entity-changes))


(defn- apply-changes [entity-db db-changes]
  (reduce (fn [db [entity-id entity-changes]]
            (update db entity-id apply-entity-changes entity-id entity-changes))
          entity-db
          db-changes))


(rf/reg-event-fx ::apply-changes
  (fn [{:keys [db]} [_ status db-changes]]
    (when (= status 200)
      {:db (update-and-bump-entity-db db db-changes apply-changes)})))


(rf/reg-event-db ::remove-entity
  (fn [db [_ entity-id]]
    (update db ::entity-db (comp (partial bump-generations-related-to
                                          #{entity-id}
                                          (::schema db)
                                          (::entity-db db))
                                 #(dissoc % entity-id)))))

(rf/reg-event-db ::remove-entities
  (fn [db [_ entity-ids]]
    (update db ::entity-db (comp (partial bump-generations-related-to
                                          entity-ids
                                          (::schema db)
                                          (::entity-db db))
                                 #(apply dissoc % entity-ids)))))


;;
;; Init:
;;


(rf/reg-event-fx ::load-schema
  (fn [_ _]
    {:comm/command {:command    :app.domain/schema
                    :method     :get
                    :on-success [::load-schema-done]}}))


(rf/reg-event-db ::load-schema-done
  (fn [db [_ _ {:keys [schema components]}]]
    (-> db
        (assoc ::schema schema)
        (assoc ::components components))))


(def entity-id? number?)

(defn get-id
  "Get id of the entity referred to by attr, or set thereof.

   Use this to get id:s in a situation where you don't necesarily have the entity referred to in
   app-db"
  [e attr]
  (entity-api/get-id e attr))
